import { useState } from "react";

import {
  Grid,
  Tooltip,
  ButtonBase,
  Paper,
  Menu,
  MenuItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { kgsToTons } from "@aclymatepackages/converters";
import {
  TextField,
  ToggleButtons,
  CategoriesAvatar
} from "@aclymatepackages/atoms";

import findSubcategory, {
  subcategories
} from "@aclymatepackages/subcategories";

import TransactionHeaderDisplay from "./TransactionHeaderDisplay";

import useFlightsInput from "../../inputs/emissions/FlightsInputBlock";
import useCombinedUtilitiesInput from "../../inputs/emissions/CombinedUtilitiesInputBlock";
import useElectricityInput from "../../inputs/emissions/ElectricityInputBlock";
import useGasInput from "../../inputs/emissions/GasInputBlock";
import useRidesInput from "../../inputs/emissions/RidesInputBlock";
import useShippingInput from "../../inputs/emissions/ShippingInputBlock";
import useMileageInput from "../../inputs/emissions/MileageInputBlock";
import useHotelInput from "../../inputs/emissions/HotelInputBlock";
import useWasteInput from "../../inputs/emissions/WasteInputBlock";
import useEquipmentFuelInput from "../../inputs/emissions/EquipmentFuelInputBlock";
import useSpendBasedInput from "../../inputs/emissions/SpendBasedInputBlock";
import useTrainInput from "../../inputs/emissions/TrainsInputBlock";
import useRefrigerantsInput from "../../inputs/emissions/RefrigerantsInput";
import usePurchasedWaterInput from "../../inputs/emissions/PurchasedWaterInputs";
import useSteamInput from "../../inputs/emissions/SteamInput";
import useChillWaterInput from "../../inputs/emissions/ChillWaterInput";

import TransactionsTagsInputs from "../../inputs/tags/TransactionTagsInputs";

import IdDisplayChip from "../../atoms/IdDisplayChip";
import DatePicker from "../../atoms/mui/DatePicker";
import SlideLayout from "../../layouts/SlideLayout";

import DocumentationUploader from "../../modules/file-uploaders/DocumentationUploader";
import TransactionNotes from "../../modules/TransactionNotes";
import TransactionDetailsFooterRow from "../../modules/TransactionDetailsFooterRow";

import {
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import { buildFormattedTagsObjs } from "../../../helpers/components/tags";
import useAccountingData from "../../../helpers/hooks/accountingData";
import usePermissions from "../../../helpers/hooks/usePermissions";
import { deriveOfficeUtilitySubcategory } from "../../../helpers/components/officeUtilitySubcategory";

const InputCategoriesMenu = ({
  categoriesMenuAnchorEl,
  setCategoriesMenuAnchorEl,
  onSubcategoryChange,
  transactionSubcategory,
  anchorOrigin = { vertical: "top", horizontal: "left" }
}) => {
  const inputSubcategories = subcategories.filter(({ input }) => input);

  return (
    <Menu
      open={!!categoriesMenuAnchorEl}
      anchorEl={categoriesMenuAnchorEl}
      onClose={() => setCategoriesMenuAnchorEl(null)}
      anchorOrigin={anchorOrigin}
    >
      {inputSubcategories.map(({ name, subcategory }) => (
        <MenuItem
          key={`${subcategory}-menu-item`}
          selected={subcategory === transactionSubcategory}
          onClick={() => onSubcategoryChange(subcategory)}
        >
          <ListItemAvatar>
            <CategoriesAvatar subcategory={subcategory} />
          </ListItemAvatar>
          <ListItemText>{name}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );
};

const IconSelect = ({ transaction, editTransaction }) => {
  const theme = useTheme();

  const { subcategory: transactionSubcategory } = transaction;

  const [categoriesMenuAnchorEl, setCategoriesMenuAnchorEl] = useState(null);

  const onSubcategoryChange = (subcategory) => {
    editTransaction("subcategory")(subcategory);
    setCategoriesMenuAnchorEl(null);
    return editTransaction("tonsCo2e")(0);
  };

  return (
    <>
      <Tooltip title="Click to recategorize this transaction">
        <ButtonBase onClick={(e) => setCategoriesMenuAnchorEl(e.currentTarget)}>
          <Paper elevation={2} style={{ padding: theme.spacing(1) }}>
            <TransactionHeaderDisplay
              transactionSubcategory={transactionSubcategory}
            />
          </Paper>
        </ButtonBase>
      </Tooltip>
      <InputCategoriesMenu
        categoriesMenuAnchorEl={categoriesMenuAnchorEl}
        setCategoriesMenuAnchorEl={setCategoriesMenuAnchorEl}
        onSubcategoryChange={onSubcategoryChange}
        transactionSubcategory={transactionSubcategory}
      />
    </>
  );
};

const NonManualKnownVendorSpendBasedToggle = ({
  subcategory: transactionSubcategory,
  vendorId,
  editTransaction
}) => {
  const [vendorsWithRelationships] = useCachedDisplayData(
    "vendor-relationships"
  );

  const relationship = vendorsWithRelationships.find(
    (vendor) => vendor?.vendorId === vendorId
  );
  const { emissionCategory: vendorCategory } = relationship?.vendorData || {};

  const {
    name: vendorCategoryName,
    icon: vendorCategoryIcon,
    naics
  } = findSubcategory(vendorCategory);
  const { code, kgsCo2ePerDollar } = naics || {};
  const { icon: spendBasedIcon } = findSubcategory("spend-based");

  const subcategoryButtonProps =
    transactionSubcategory !== "spend-based"
      ? {
          avatar: <CategoriesAvatar subcategory={vendorCategory} size="small" />
        }
      : { icon: vendorCategoryIcon };

  const spendBasedCategoryProps =
    transactionSubcategory === "spend-based"
      ? { avatar: <CategoriesAvatar subcategory="spend-based" size="small" /> }
      : { icon: spendBasedIcon };

  const onToggle = (subcategory) => {
    editTransaction("subcategory")(subcategory);

    if (subcategory === "spend-based") {
      editTransaction("naicsCode")(code);
      editTransaction("tonsCo2ePerDollar")(kgsToTons(kgsCo2ePerDollar));
      return editTransaction("scopeThreeCategory")(1);
    }
  };

  return (
    <ToggleButtons
      value={transactionSubcategory}
      onChange={onToggle}
      buttons={[
        {
          value: vendorCategory,
          name: vendorCategoryName,
          ...subcategoryButtonProps
        },
        {
          value: "spend-based",
          name: "Spend Based",
          ...spendBasedCategoryProps
        }
      ]}
    />
  );
};

const HeaderInput = ({ transaction, editTransaction }) => {
  const { vendorId, id, subcategory, source, status } = transaction;
  const [vendorsWithRelationships] = useCachedDisplayData(
    "vendor-relationships"
  );
  const relationship = vendorsWithRelationships.find(
    (vendor) => vendor?.vendorId === vendorId
  );
  const { emissionCategory } = relationship?.vendorData || {};
  const { naics } = findSubcategory(emissionCategory);

  if (
    !id ||
    status === "locked" ||
    (vendorId &&
      (emissionCategory === "spend-based" ||
        source === "manual-entry" ||
        !naics))
  ) {
    return <TransactionHeaderDisplay transactionSubcategory={subcategory} />;
  }

  if (!vendorId) {
    return (
      <IconSelect transaction={transaction} editTransaction={editTransaction} />
    );
  }

  return (
    <NonManualKnownVendorSpendBasedToggle
      editTransaction={editTransaction}
      {...transaction}
    />
  );
};

const TransactionDetailsBlock = ({
  transaction,
  originalTransaction = {},
  editTransaction,
  saveTransaction,
  setIsSlideOpen,
  onRejectTransaction,
  saveButtonText,
  slideLeftPanel,
  closeOnSave = true,
  isButtonLoading,
  showDocumentationTab,
  setEmissions
}) => {
  const {
    displayName,
    description,
    vendorId,
    vendorName,
    subcategory,
    inputSubcategory,
    date,
    employees = [],
    office = {},
    source,
    amount,
    value,
    name,
    id,
    status,
    warning,
    notes = []
  } = transaction;

  const {
    employees: originalEmployees = [],
    office: originalOffice = {},
    vendorId: originalVendorId
  } = originalTransaction;

  const [{ earliestNewEmissionDate }] = useAccountingData();
  const [{ startDate }] = useAccountData();
  const [companyEmployees] = useCachedDisplayData("employees");
  const [companyOffices] = useCachedDisplayData("offices");
  const { canEdit } = usePermissions();

  const [calcLoading, setCalcLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("inputs");

  const inputHookProps = {
    setCalcLoading,
    transaction,
    editTransaction,
    onSave: async (updateObj) => {
      const baseSubcategory = inputSubcategory || subcategory;
      const {
        subcategory: derivedSubcategory,
        inputSubcategory: derivedInputSubcategory
      } = deriveOfficeUtilitySubcategory({
        offices: companyOffices,
        officeId: office?.id,
        subcategory: baseSubcategory,
        date
      });

      const newTransactionId = await saveTransaction({
        archived: false,
        date,
        name,
        subcategory: derivedSubcategory,
        source,
        status: "confirmed",
        amount: amount || value,
        description,
        vendorId,
        vendorName,
        ...buildFormattedTagsObjs(transaction),
        ...(derivedInputSubcategory
          ? { inputSubcategory: derivedInputSubcategory }
          : {}),
        ...updateObj
      });

      if (closeOnSave) {
        setIsSlideOpen(false);
      }

      return newTransactionId;
    }
  };

  const flightsInputProps = useFlightsInput(inputHookProps);
  const equipmentFuelInputProps = useEquipmentFuelInput(inputHookProps);
  const combinedUtilitiesInputProps = useCombinedUtilitiesInput(inputHookProps);
  const gasInputProps = useGasInput(inputHookProps);
  const electricUtilityInputProps = useElectricityInput(inputHookProps);
  const ridesInputProps = useRidesInput(inputHookProps);
  const shippingInputProps = useShippingInput(inputHookProps);
  const mileageInputProps = useMileageInput(inputHookProps);
  const hotelInputProps = useHotelInput(inputHookProps);
  const wasteInputProps = useWasteInput(inputHookProps);
  const trainInputProps = useTrainInput(inputHookProps);
  const spendBasedInputProps = useSpendBasedInput(inputHookProps);
  const refrigerantsInputProps = useRefrigerantsInput(inputHookProps);
  const purchasedWaterInputProps = usePurchasedWaterInput(inputHookProps);
  const steamInputProps = useSteamInput(inputHookProps);
  const chillWaterInputProps = useChillWaterInput(inputHookProps);

  const subcategoryInputs = {
    flights: flightsInputProps,
    hotels: hotelInputProps,
    fuel: mileageInputProps,
    "equipment-fuel": equipmentFuelInputProps,
    "rented-equipment": equipmentFuelInputProps,
    "rental-cars": mileageInputProps,
    "company-vehicles": mileageInputProps,
    utilities: combinedUtilitiesInputProps,
    gas: gasInputProps,
    electricity: electricUtilityInputProps,
    rides: ridesInputProps,
    shipping: shippingInputProps,
    waste: wasteInputProps,
    trains: trainInputProps,
    "spend-based": spendBasedInputProps,
    refrigerants: refrigerantsInputProps,
    "purchased-water": purchasedWaterInputProps,
    steam: steamInputProps,
    "chill-water": chillWaterInputProps,
    // Purchased heat is calculated from the fuel burned to produce it, so it
    // uses the same fuel-type + quantity input as a gas bill.
    "purchased-heat": gasInputProps,
    "purchased-goods-and-services": spendBasedInputProps,
    "capital-goods": spendBasedInputProps,
    "upstream-transportation-and-distribution": spendBasedInputProps,
    "business-travel": spendBasedInputProps,
    "downstream-transportation-and-distribution": spendBasedInputProps,
    "waste-generated-in-operations": spendBasedInputProps
  };

  const {
    onTransactionSave,
    inputBlock,
    saveEnabled,
    tonsCo2e: inputTonsCo2e
  } = subcategoryInputs[inputSubcategory || subcategory] || {};

  const tonsCo2e = Number(inputTonsCo2e) || transaction.tonsCo2e || 0;

  const isSaveEnabled = () => {
    if (!tonsCo2e && !saveEnabled) {
      return false;
    }

    if (slideLeftPanel) {
      return saveEnabled;
    }

    const { id: originalOfficeId } = originalOffice || {};
    const { id: officeId } = office || {};
    const areEmployeesSame = originalEmployees.reduce(
      (acc, { id }) => acc && employees.find((employee) => employee.id === id),
      true
    );

    const tagsChanged =
      originalOfficeId !== officeId ||
      originalVendorId !== vendorId ||
      !areEmployeesSame;

    return tagsChanged || saveEnabled;
  };

  const generateMinDate = () => {
    if (status === "locked") {
      return null;
    }

    if (earliestNewEmissionDate) {
      return earliestNewEmissionDate;
    }

    const { id: officeId } = office || {};
    const employeesIds = employees.map(({ id: employeeId }) => employeeId);

    if (officeId) {
      const { startDate: officeStartDate = startDate } = companyOffices.length
        ? companyOffices.find(({ id }) => id === officeId) || {}
        : {};

      return officeStartDate;
    }

    if (employeesIds) {
      const employeesStartDates = companyEmployees
        .filter(({ id }) => employeesIds.includes(id))
        .map(({ startDate }) => startDate);

      return employeesStartDates.sort((a, b) => b - a)[0] || startDate;
    }

    return startDate;
  };

  const tabs = {
    inputs: (
      <Grid container direction="column" spacing={2}>
        <Grid item container spacing={2}>
          {subcategory !== "flights" && (
            <Grid item xs={5}>
              <DatePicker
                label="Emission Date"
                date={date}
                editDate={editTransaction("date")}
                minDate={generateMinDate()}
                maxDate={subcategory !== "hotels" ? new Date() : undefined}
                disabled={status === "locked"}
              />
            </Grid>
          )}
          <Grid item xs={subcategory !== "flights" ? 7 : 12}>
            <TextField
              label="Emission Name"
              large
              value={name}
              setValue={editTransaction("name")}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <Tooltip title="We use the vendor name as a default name for every transaction, but you can use this field to put in a custom name for this transaction.">
                      <FontAwesomeIcon icon={faQuestionCircle} size="2x" />
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              disabled={status === "locked"}
            />
          </Grid>
        </Grid>
        <Grid item>
          <TransactionsTagsInputs
            transaction={transaction}
            editTransaction={editTransaction}
          />
        </Grid>
        <Grid item>{inputBlock}</Grid>
        {!!Object.keys(transaction.metadata || {}).length && (
          <Grid item container direction="column" spacing={1}>
            <Grid item>
              <Typography variant="subtitle1">Import Metadata</Typography>
            </Grid>
            {Object.entries(transaction.metadata).map(
              ([fieldName, fieldValue]) => (
                <Grid
                  item
                  container
                  justifyContent="space-between"
                  key={fieldName}
                >
                  <Grid item>
                    <Typography variant="subtitle2">{fieldName}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      {fieldValue}
                    </Typography>
                  </Grid>
                </Grid>
              )
            )}
          </Grid>
        )}
      </Grid>
    ),
    documentation: (
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <TransactionNotes
            collection="transactions"
            docId={id}
            notes={notes}
            editField={editTransaction("notes")}
            setListData={setEmissions}
          />
        </Grid>
        <Grid item>
          <DocumentationUploader
            type="transactions"
            docId={id}
            docName={displayName}
          />
        </Grid>
      </Grid>
    )
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={setIsSlideOpen}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <HeaderInput
              transaction={transaction}
              editTransaction={editTransaction}
            />
          </Grid>
          {id && (
            <Grid item>
              <IdDisplayChip id={id} type="emission" />
            </Grid>
          )}
        </Grid>
      }
      tagInputs={
        showDocumentationTab && (
          <>
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {[
                { label: "Inputs", value: "inputs" },
                { label: "Documentation", value: "documentation" }
              ].map(({ label, value }, idx) => (
                <Tab
                  label={label}
                  value={value}
                  key={`transaction-input-tab-${idx}`}
                />
              ))}
            </Tabs>
            <Divider />
          </>
        )
      }
      content={tabs[selectedTab]}
      footer={
        <TransactionDetailsFooterRow
          saveEnabled={isSaveEnabled()}
          saveTransaction={onTransactionSave}
          tonsCo2e={tonsCo2e}
          acceptText={saveButtonText}
          onRejectTransaction={onRejectTransaction}
          calcLoading={isButtonLoading || calcLoading}
          source={source}
          disabled={
            status === "locked" || source === "recurring-closed" || !canEdit
          }
          alert={warning && warning.message}
        />
      }
      leftContent={slideLeftPanel}
    />
  );
};
export default TransactionDetailsBlock;
